import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    MessageCircleQuestion, X, Send, ThumbsUp, ThumbsDown, PlayCircle, Loader2, BookOpen, AlertTriangle,
} from 'lucide-react';
import type { AppView } from '../../types';
import type { TutorialKapitelId, TutorialRolle } from '../../tutorial/types';
import { TUTORIAL_REGISTER, kapitelNachId } from '../../tutorial/register';
import { stolperfallenFuer } from '../../tutorial/stolperfallenLoader';
import { MAX_KONTEXT_KAPITEL, kontextFuerFrage } from '../../services/tutorial/hilfeChatLogic';
import type { HilfeKapitelKontext } from '../../services/tutorial/hilfeChatLogic';
import { bewerteAntwort, frageStellen } from '../../services/tutorial/hilfeChatService';

/**
 * LERNZENTRUM — der Chat „Frag OS-IA" (Plan §1.8 und §2.1, Paket E).
 *
 * Ein runder Knopf unten rechts, darueber dem kontextsensitiven „?"
 * (`KontextHilfe`, dasselbe Eck). Ein Klick oeffnet ein schmales Fenster: Frage
 * eintippen, Antwort lesen, ueber die Kapitel-Chips ins Lernzentrum springen
 * oder die Tour starten.
 *
 * ─── Drei Eigenschaften, die alle drei Absicht sind ─────────────────────────
 *
 *  1. **GEERDET.** Der Kontext ist das Register im Bundle — Titel,
 *     Bedienschritte, Stolperfallen. Keine Projektdaten, kein Bericht, keine
 *     Rechnung; die Komponente bekommt sie gar nicht erst gereicht. Was die
 *     KI nicht in den Kapiteln findet, sagt sie, statt es zu erfinden.
 *
 *  2. **NICHT PERSISTENT.** Der Verlauf lebt im State und ist mit dem
 *     Schliessen der Seite weg. Gespeichert wird nur Frage + Antwort +
 *     Kapitelbezug in `tutorial_fragen` — und zwar fuer die Lueckenliste der
 *     Verwaltung, nicht als Gespraechsarchiv.
 *
 *  3. **EIGENSTAENDIG.** Faellt die KI aus, faellt nur der Chat aus. Der
 *     Service wirft nicht; er meldet `ok: false` mit einem Satz, der hier
 *     angezeigt wird. Die Kapitel im Lernzentrum bleiben vollstaendig lesbar.
 *
 * Der Einbau in `App.tsx` gehoert einem anderen Auftrag — diese Datei
 * exportiert nur die Komponente und ihre Props.
 */

interface Props {
    /** Die gerade offene Ansicht. Ihre Kapitel stehen im Kontext vorn. */
    view: AppView;
    /** Lernrollen des angemeldeten Profils (`rollenAusProfil`). Leer = kein Kontext. */
    rollen: TutorialRolle[];
    /** Klick auf einen Kapitel-Chip: ins Lernzentrum, direkt auf das Kapitel. */
    onOeffneKapitel: (kapitelId: TutorialKapitelId) => void;
    /** Klick auf „Tour starten": Ansicht wechseln und die Tour dort starten. */
    onStarteTour: (kapitelId: TutorialKapitelId) => void;
    /** „Dazu fragen" von einer Kapitelseite: oeffnet den Chat mit diesem Kapitel im Rücken. */
    vorbelegtesKapitel?: TutorialKapitelId;
}

type Sprecher = 'nutzer' | 'ki';

interface Eintrag {
    id: number;
    sprecher: Sprecher;
    text: string;
    kapitelIds?: TutorialKapitelId[];
    tourStart?: TutorialKapitelId;
    /** Zeile in `tutorial_fragen`; fehlt sie, gibt es keine Daumen. */
    frageId?: string | null;
    bewertung?: boolean;
    fehler?: boolean;
}

/** Titel eines Kapitels fuer die Chips. Unbekannte IDs kommen hier nie an (Filter im Service). */
const titelVon = (id: TutorialKapitelId): string => kapitelNachId(id)?.titel ?? id;

export const HilfeChat: React.FC<Props> = ({ view, rollen, onOeffneKapitel, onStarteTour, vorbelegtesKapitel }) => {
    const [offen, setOffen] = useState(false);
    const [eingabe, setEingabe] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [verlauf, setVerlauf] = useState<Eintrag[]>([]);
    const naechsteId = useRef(1);
    const endeRef = useRef<HTMLDivElement | null>(null);
    const feldRef = useRef<HTMLTextAreaElement | null>(null);

    /**
     * Der Kontext haengt nur an Ansicht, Rollen und dem vorbelegten Kapitel —
     * er wird deshalb gemerkt und nicht bei jedem Tastendruck neu gebaut
     * (20 Kapitel mit Stolperfallen sind billig, aber nicht umsonst).
     *
     * Das vorbelegte Kapitel rutscht nach vorn: wer von der Kapitelseite aus
     * fragt, meint dieses Kapitel, auch wenn er gerade in einer anderen
     * Ansicht steht.
     */
    const kontext = useMemo<HilfeKapitelKontext[]>(() => {
        const basis = kontextFuerFrage(view, rollen, TUTORIAL_REGISTER, stolperfallenFuer);
        if (!vorbelegtesKapitel) return basis;

        const drin = basis.find((k) => k.id === vorbelegtesKapitel);
        if (drin) return [drin, ...basis.filter((k) => k.id !== vorbelegtesKapitel)];

        // Kapitel einer anderen Ansicht: einzeln bauen und vorn anhaengen. Die
        // Rollenpruefung macht `kontextFuerFrage` — ein Kapitel, das diese
        // Rolle nicht sehen darf, kommt auch hier nicht durch.
        const k = kapitelNachId(vorbelegtesKapitel);
        if (!k) return basis;
        const [einzeln] = kontextFuerFrage(k.view, rollen, [k], stolperfallenFuer);
        return einzeln ? [einzeln, ...basis].slice(0, MAX_KONTEXT_KAPITEL) : basis;
    }, [view, rollen, vorbelegtesKapitel]);

    /** „Dazu fragen" von der Kapitelseite: Fenster auf, Frage angetippt. */
    useEffect(() => {
        if (!vorbelegtesKapitel) return;
        const k = kapitelNachId(vorbelegtesKapitel);
        setOffen(true);
        setEingabe((alt) => (alt.trim() ? alt : k ? `Zu „${k.titel}": ` : ''));
        // Der Fokus gehoert ans Ende des vorbelegten Textes, nicht an den Anfang.
        window.setTimeout(() => {
            const feld = feldRef.current;
            if (!feld) return;
            feld.focus();
            feld.setSelectionRange(feld.value.length, feld.value.length);
        }, 0);
    }, [vorbelegtesKapitel]);

    /** Neue Zeilen sichtbar halten. `scrollIntoView` nur im Fenster, nie global. */
    useEffect(() => {
        if (offen) endeRef.current?.scrollIntoView({ block: 'end' });
    }, [verlauf, offen, laeuft]);

    const anhaengen = (eintrag: Omit<Eintrag, 'id'>) => {
        const id = naechsteId.current++;
        setVerlauf((alt) => [...alt, { ...eintrag, id }]);
        return id;
    };

    const senden = async () => {
        const frage = eingabe.trim();
        if (!frage || laeuft) return;
        setEingabe('');
        anhaengen({ sprecher: 'nutzer', text: frage });
        setLaeuft(true);
        try {
            const ergebnis = await frageStellen({ frage, view, rollen, kapitel: kontext });
            anhaengen(ergebnis.ok
                ? {
                    sprecher: 'ki',
                    text: ergebnis.antwort,
                    kapitelIds: ergebnis.kapitelIds,
                    tourStart: ergebnis.tourStart,
                    frageId: ergebnis.frageId,
                }
                : {
                    sprecher: 'ki',
                    text: ergebnis.fehler || 'Die KI ist gerade nicht erreichbar.',
                    fehler: true,
                });
        } finally {
            setLaeuft(false);
        }
    };

    /**
     * Daumen. Die Anzeige folgt sofort, das Schreiben darf scheitern (Tabelle
     * fehlt, RLS) — dann bleibt es bei der Anzeige. Eine Bewertung ist eine
     * Randnotiz fuer die Kuratierung, kein Nachweis.
     */
    const bewerten = async (eintragId: number, frageId: string | null | undefined, hilfreich: boolean) => {
        setVerlauf((alt) => alt.map((e) => (e.id === eintragId ? { ...e, bewertung: hilfreich } : e)));
        await bewerteAntwort(frageId, hilfreich);
    };

    const aufTaste = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sendet, Shift+Enter macht einen Absatz — wie in jedem Chat.
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void senden();
        }
    };

    return (
        <>
            {/* ── Der Knopf ──
                Unten rechts ueber dem „?" von `KontextHilfe` (bottom-4). Beide
                Einstiege stehen im selben Eck, aber nie uebereinander. */}
            <button
                type="button"
                onClick={() => setOffen((o) => !o)}
                aria-label="Frag OS-IA"
                aria-expanded={offen}
                title="Frag OS-IA"
                className="hidden md:flex fixed bottom-[4.25rem] right-4 z-[115] w-10 h-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 border border-blue-700 shadow-lg transition-colors"
            >
                {offen ? <X className="w-5 h-5" /> : <MessageCircleQuestion className="w-5 h-5" />}
            </button>

            {/* ── Das Fenster ──
                Nur auf dem Desktop, wie die Seitenleiste: mobil verdeckte es den
                halben Bildschirm, und die mobile Hilfe ist im Plan (§2.4) ein
                anderer Einstieg. */}
            {offen && (
                <section
                    aria-label="Frag OS-IA"
                    className="hidden md:flex fixed bottom-4 right-4 z-[114] w-[380px] max-h-[70vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex-col overflow-hidden animate-slide-up"
                >
                    <div className="shrink-0 px-4 py-3 border-b border-slate-200 flex items-start gap-2.5">
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 shrink-0">
                            <MessageCircleQuestion className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-sm font-bold text-slate-800 leading-tight">Frag OS-IA</h2>
                            <p className="text-[11px] text-slate-500">
                                Antwortet nur aus der Hilfe — nie aus deinen Projektdaten.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOffen(false)}
                            aria-label="Chat schließen"
                            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* ── Verlauf ── */}
                    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
                        {verlauf.length === 0 && !laeuft && (
                            <div className="px-1 py-6 text-center">
                                <BookOpen className="w-6 h-6 mx-auto text-slate-300 mb-2" />
                                <p className="text-sm font-semibold text-slate-600">Was möchtest du wissen?</p>
                                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                                    {kontext.length > 0
                                        ? `${kontext.length} Kapitel stehen im Hintergrund bereit. Steht die Antwort dort nicht, sagt die KI genau das.`
                                        : 'Für deine Rolle ist noch kein Kapitel hinterlegt — die Antwort bleibt deshalb dünn.'}
                                </p>
                            </div>
                        )}

                        {verlauf.map((e) => (
                            <div key={e.id} className={e.sprecher === 'nutzer' ? 'flex justify-end' : ''}>
                                <div
                                    className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-line ${e.sprecher === 'nutzer'
                                        ? 'bg-blue-600 text-white'
                                        : e.fehler
                                            ? 'bg-red-50 text-red-900 border border-red-200'
                                            : 'bg-white text-slate-700 border border-slate-200'}`}
                                >
                                    {e.fehler && <AlertTriangle className="w-4 h-4 text-red-500 inline-block mr-1.5 -mt-0.5" />}
                                    {e.text}
                                </div>

                                {/* ── Kapitel-Chips, Tour und Daumen ── */}
                                {e.sprecher === 'ki' && !e.fehler && (
                                    <div className="mt-1.5 space-y-1.5">
                                        {!!e.kapitelIds?.length && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {e.kapitelIds.map((id) => (
                                                    <button
                                                        key={id}
                                                        type="button"
                                                        onClick={() => onOeffneKapitel(id)}
                                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-2 py-1 transition-colors"
                                                    >
                                                        <BookOpen className="w-3 h-3" />{titelVon(id)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex items-center gap-1.5">
                                            {e.tourStart && (
                                                <button
                                                    type="button"
                                                    onClick={() => onStarteTour(e.tourStart as TutorialKapitelId)}
                                                    className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-slate-200 transition-colors"
                                                >
                                                    <PlayCircle className="w-3.5 h-3.5 text-blue-600" />Tour starten
                                                </button>
                                            )}
                                            {/* Daumen nur, wenn die Frage wirklich abgelegt wurde —
                                                sonst zeigte der Knopf eine Wirkung vor, die es nicht gibt. */}
                                            {e.frageId && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => void bewerten(e.id, e.frageId, true)}
                                                        aria-label="Antwort war hilfreich"
                                                        aria-pressed={e.bewertung === true}
                                                        className={`p-1 rounded-lg border transition-colors ${e.bewertung === true
                                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                                                            : 'text-slate-400 bg-white border-slate-200 hover:text-emerald-600'}`}
                                                    >
                                                        <ThumbsUp className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void bewerten(e.id, e.frageId, false)}
                                                        aria-label="Antwort war nicht hilfreich"
                                                        aria-pressed={e.bewertung === false}
                                                        className={`p-1 rounded-lg border transition-colors ${e.bewertung === false
                                                            ? 'text-red-700 bg-red-50 border-red-200'
                                                            : 'text-slate-400 bg-white border-slate-200 hover:text-red-600'}`}
                                                    >
                                                        <ThumbsDown className="w-3.5 h-3.5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {laeuft && (
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />Sucht in der Hilfe …
                            </div>
                        )}
                        <div ref={endeRef} />
                    </div>

                    {/* ── Eingabe ── */}
                    <div className="shrink-0 border-t border-slate-200 p-2.5 bg-white">
                        <div className="flex items-end gap-2">
                            <textarea
                                ref={feldRef}
                                value={eingabe}
                                onChange={(ev) => setEingabe(ev.target.value)}
                                onKeyDown={aufTaste}
                                rows={2}
                                aria-label="Deine Frage"
                                placeholder="Frage zur Bedienung … (Enter sendet)"
                                className="flex-1 resize-none text-sm text-slate-700 placeholder:text-slate-400 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                            />
                            <button
                                type="button"
                                onClick={() => void senden()}
                                disabled={laeuft || !eingabe.trim()}
                                aria-label="Frage senden"
                                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
                            >
                                {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1.5 px-1">
                            Der Verlauf ist nicht gespeichert. Frage und Antwort landen in der Hilfe-Statistik.
                        </p>
                    </div>
                </section>
            )}
        </>
    );
};
